import { forwardRef, useEffect, useRef, useState } from 'react';
import type { TAdfoxBannerOnLoadData, TAdfoxParameters, TAdfoxTemplatesParams } from 'shared/types/adfox';
import { ensureAdfoxApiAvailable } from 'shared/utils/ensureAdfoxApiAvailable';
import { getCanRunAds } from 'shared/utils/getCanRunAds';
import { getParsedDecodedParams } from 'shared/utils/postViewAnalytics/getParsedDecodedParams';
import { useApplicationContext } from 'shared/utils/applicationContext/applicationContext';

import { AdfoxTemplates } from '../AdfoxTemplates';
import { useAdfoxBannerContext } from '../AdfoxBannerContext';

export const AdfoxBanner = forwardRef<HTMLDivElement>((_props, ref) => {
  const applicationContext = useApplicationContext();
  const { config, logger } = applicationContext;

  const { params, sizes, eventEmitter, containerId } = useAdfoxBannerContext();

  const [adfoxBannerOnLoadData, setAdfoxBannerOnLoadData] = useState<TAdfoxBannerOnLoadData | null>(null);
  const adfoxBannerOnLoadDataRef = useRef<TAdfoxBannerOnLoadData>(null);
  const parsedDecodedParamsRef = useRef<TAdfoxTemplatesParams>(null);

  useEffect(() => {
    if (!getCanRunAds()) {
      return;
    }

    /**
     * Promise загрузки context.js живёт на уровне модуля (singleton) и не привязан к жизни компонента.
     * Если компонент размонтируется до того, как скрипт догрузится, .then всё равно выполнится позже —
     * без флага мы вызовем setState на размонтированном компоненте и создадим баннер, который уже некому уничтожить
     * (cleanup отработал, когда createdContainerId был ещё null).
     */
    let cancelled = false;

    ensureAdfoxApiAvailable({ applicationContext })
      .then(() => {
        if (cancelled || !window.Ya?.adfoxCode) {
          return;
        }

        const adfoxParameters: TAdfoxParameters = {
          containerId,
          lazyLoad: {
            fetchMargin: 100,
            mobileScaling: 1,
          },
          onError: error => {
            eventEmitter.emit({ containerId, error, event: 'error' });
          },
          onLoad: data => {
            const parsedDecodedParams = getParsedDecodedParams(data, logger);

            parsedDecodedParamsRef.current = parsedDecodedParams;
            adfoxBannerOnLoadDataRef.current = data;

            eventEmitter.emit({ containerId, data, event: 'load', parsedDecodedParams });
            setAdfoxBannerOnLoadData(data);
          },
          onRender: () => {
            eventEmitter.emit({
              containerId,
              data: adfoxBannerOnLoadDataRef.current as TAdfoxBannerOnLoadData,
              event: 'render',
              parsedDecodedParams: parsedDecodedParamsRef.current,
            });
          },
          onStub: () => {
            eventEmitter.emit({ containerId, event: 'stub' });
          },
          ownerId: Number(config.getStrict('MarTech.AdBanner.AdfoxOwnerId')),
          params,
        };

        window.Ya.adfoxCode.create(adfoxParameters);
      })
      .catch(() => {
        /* ensureAdfoxApiAvailable уже залогировал ошибку */
      });

    return (): void => {
      cancelled = true;

      if (window.Ya?.adfoxCode) {
        window.Ya.adfoxCode.destroy(containerId);
      }
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdfoxTemplates
      sizes={sizes}
      adfoxBannerOnLoadData={adfoxBannerOnLoadData}
      parsedDecodedParams={parsedDecodedParamsRef.current}
      ref={ref}
    />
  );
});

AdfoxBanner.displayName = 'AdfoxBanner';
